/**
 * NotificationPreferencesPage - Issue #319
 * 
 * Settings page for managing email, SMS, and push notification preferences
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Mail, MessageSquare, Smartphone, Check, AlertCircle } from 'lucide-react';
import { useNotificationPreferences, useUpdateNotificationPreferences } from '../hooks/useNotifications';
import { TopBar, Button, Alert } from '../components';
import { Skeleton } from '../components/ui/Skeleton';

interface PreferenceToggleProps {
  id: string;
  label: string;
  description: string;
  icon: React.ReactNode;
  enabled: boolean;
  onChange: (enabled: boolean) => void;
  disabled?: boolean;
  comingSoon?: boolean;
}

function PreferenceToggle({ 
  id, 
  label, 
  description, 
  icon, 
  enabled, 
  onChange, 
  disabled = false,
  comingSoon = false 
}: PreferenceToggleProps) {
  return (
    <div className={`flex items-center gap-4 p-4 bg-oro-surface border border-oro-border rounded-xl ${disabled ? 'opacity-50' : ''}`}>
      <div className="shrink-0 w-10 h-10 bg-oro-surface-alt rounded-lg flex items-center justify-center">
        {icon}
      </div>
      
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <h3 className="font-medium text-text-primary">{label}</h3>
          {comingSoon && (
            <span className="text-xs px-2 py-1 bg-accent/20 text-accent rounded-full">
              Coming Soon
            </span>
          )}
        </div>
        <p className="text-sm text-text-secondary mt-1">{description}</p>
      </div>
      
      <button
        type="button"
        role="switch"
        aria-checked={enabled}
        aria-labelledby={id}
        disabled={disabled}
        onClick={() => !disabled && onChange(!enabled)}
        className={`
          relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2
          ${enabled && !disabled
            ? 'bg-accent'
            : 'bg-oro-surface-alt'
          }
          ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}
        `}
      >
        <span
          className={`
            inline-block h-4 w-4 transform rounded-full bg-white transition duration-200
            ${enabled && !disabled ? 'translate-x-6' : 'translate-x-1'}
          `}
        />
      </button>
    </div>
  );
}

export default function NotificationPreferencesPage() {
  const navigate = useNavigate();
  const [hasChanges, setHasChanges] = useState(false);
  const [localPrefs, setLocalPrefs] = useState<Record<string, boolean>>({});
  
  const { data: preferencesData, isLoading } = useNotificationPreferences();
  const updatePreferences = useUpdateNotificationPreferences();

  const preferences = preferencesData?.preferences;

  // Get current value (local override or server value)
  const getValue = (key: string): boolean => {
    if (!preferences) return false;
    const localValue = localPrefs[key];
    const serverValue = preferences[key as keyof typeof preferences];
    return localValue !== undefined ? Boolean(localValue) : Boolean(serverValue);
  };

  // Update local state and mark changes
  const handleToggle = (key: string, value: boolean) => {
    setLocalPrefs(prev => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };

  // Save changes to server
  const handleSave = async () => {
    if (!hasChanges || Object.keys(localPrefs).length === 0) return;

    try {
      await updatePreferences.mutateAsync(localPrefs);
      setLocalPrefs({});
      setHasChanges(false);
    } catch (error) {
      // Development-only logging, user gets feedback via mutation error state
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to save preferences:', error);
      }
    }
  };

  // Reset local changes
  const handleReset = () => {
    setLocalPrefs({});
    setHasChanges(false);
  };

  if (isLoading) {
    return (
      <div className="flex flex-col bg-oro-bg min-h-screen">
        <TopBar title="Notification Preferences" onBack={() => navigate(-1)} />
        <div className="flex-1 p-4">
          <div className="space-y-4">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="flex items-center gap-4 p-4 bg-oro-surface border border-oro-border rounded-xl">
                <Skeleton variant="circular" className="w-10 h-10" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-3 w-48" />
                </div>
                <Skeleton className="w-11 h-6 rounded-full" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!preferences) {
    return (
      <div className="flex flex-col bg-oro-bg min-h-screen">
        <TopBar title="Notification Preferences" onBack={() => navigate(-1)} />
        <div className="flex-1 p-4">
          <Alert
            variant="error"
            title="Failed to load preferences">
            Unable to fetch your notification settings. Please try again.
          </Alert>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col bg-oro-bg min-h-screen">
      <TopBar title="Notification Preferences" onBack={() => navigate(-1)} />
      
      <div className="flex-1 p-4 pb-24">
        <div className="space-y-6">
          {/* Email Notifications */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Mail className="w-5 h-5 text-accent" />
              <h2 className="text-lg font-semibold text-text-primary">Email Notifications</h2>
            </div>
            
            <div className="space-y-3">
              <PreferenceToggle
                id="email_invites"
                label="Club Invitations"
                description="Get notified when you're invited to join a club"
                icon={<Mail className="w-5 h-5 text-accent" />}
                enabled={getValue('email_invites')}
                onChange={(value) => handleToggle('email_invites', value)}
              />
              
              <PreferenceToggle
                id="email_event_reminders"
                label="Event Reminders"
                description="Reminders for upcoming events you've RSVP'd to"
                icon={<Bell className="w-5 h-5 text-accent" />}
                enabled={getValue('email_event_reminders')}
                onChange={(value) => handleToggle('email_event_reminders', value)}
              />
              
              <PreferenceToggle
                id="email_rsvp_confirmations"
                label="RSVP Confirmations"
                description="Confirmation emails when you RSVP to events"
                icon={<Check className="w-5 h-5 text-green-500" />}
                enabled={getValue('email_rsvp_confirmations')}
                onChange={(value) => handleToggle('email_rsvp_confirmations', value)}
              />
              
              <PreferenceToggle
                id="email_event_updates"
                label="Event Updates"
                description="Get notified when event details change"
                icon={<AlertCircle className="w-5 h-5 text-yellow-500" />}
                enabled={getValue('email_event_updates')}
                onChange={(value) => handleToggle('email_event_updates', value)}
              />
              
              <PreferenceToggle
                id="email_club_updates"
                label="Club Announcements"
                description="Important updates from your club leaders"
                icon={<MessageSquare className="w-5 h-5 text-blue-500" />}
                enabled={getValue('email_club_updates')}
                onChange={(value) => handleToggle('email_club_updates', value)}
              />
              
              <PreferenceToggle
                id="email_digest"
                label="Weekly Digest"
                description="Summary of club activity and upcoming events"
                icon={<Mail className="w-5 h-5 text-purple-500" />}
                enabled={getValue('email_digest')}
                onChange={(value) => handleToggle('email_digest', value)}
              />
            </div>
          </div>

          {/* Future Notifications */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Smartphone className="w-5 h-5 text-text-muted" />
              <h2 className="text-lg font-semibold text-text-primary">Mobile Notifications</h2>
            </div>
            
            <div className="space-y-3">
              <PreferenceToggle
                id="push_enabled"
                label="Push Notifications"
                description="Real-time notifications on your device"
                icon={<Bell className="w-5 h-5 text-text-muted" />}
                enabled={getValue('push_enabled')}
                onChange={(value) => handleToggle('push_enabled', value)}
                disabled={true}
                comingSoon={true}
              />
              
              <PreferenceToggle
                id="sms_enabled"
                label="SMS Notifications"
                description="Text message notifications for urgent updates"
                icon={<MessageSquare className="w-5 h-5 text-text-muted" />}
                enabled={getValue('sms_enabled')}
                onChange={(value) => handleToggle('sms_enabled', value)}
                disabled={true}
                comingSoon={true}
              />
            </div>
          </div>

          {/* Help Text */}
          <div className="bg-oro-surface-alt border border-oro-border rounded-xl p-4">
            <p className="text-sm text-text-secondary">
              You can change these preferences anytime. Critical notifications like security alerts 
              will always be sent regardless of your settings.
            </p>
          </div>
        </div>
      </div>

      {/* Save/Reset Bar */}
      {hasChanges && (
        <div className="fixed bottom-0 left-0 right-0 bg-oro-surface border-t border-oro-border p-4 safe-area-bottom">
          <div className="flex gap-3">
            <Button
              variant="secondary"
              onClick={handleReset}
              disabled={updatePreferences.isPending}
              className="flex-1"
            >
              Reset
            </Button>
            <Button
              onClick={handleSave}
              disabled={updatePreferences.isPending}
              className="flex-1"
            >
              {updatePreferences.isPending ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}